import { useCallback, useEffect, useRef, useState } from 'react';

const getRecognitionCtor = () =>
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

/**
 * Browser speech-to-text (Chrome, Edge, Safari). No OpenAI cost.
 * Keeps the mic session alive across browser auto-pause/end events until explicit stop().
 */
export function useSpeechToText({ onTranscript, onListeningChange, lang = 'en-US' } = {}) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);
  const onListeningChangeRef = useRef(onListeningChange);
  const sessionIdRef = useRef(0);
  const keepListeningRef = useRef(false);
  const manualStopRef = useRef(false);
  const restartTimerRef = useRef(null);

  onTranscriptRef.current = onTranscript;
  onListeningChangeRef.current = onListeningChange;

  useEffect(() => {
    setSupported(Boolean(getRecognitionCtor()));
  }, []);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);

  const setListeningState = useCallback((active) => {
    setListening(active);
    onListeningChangeRef.current?.(active);
  }, []);

  const stop = useCallback(() => {
    manualStopRef.current = true;
    keepListeningRef.current = false;
    clearRestartTimer();
    try {
      recognitionRef.current?.stop();
    } catch (_) {
      /* ignore */
    }
    recognitionRef.current = null;
    setListeningState(false);
  }, [clearRestartTimer, setListeningState]);

  const attachRecognitionHandlers = useCallback(
    (recognition, sessionId) => {
      recognition.onresult = (event) => {
        if (sessionId !== sessionIdRef.current) {
          return;
        }

        let committed = '';
        let interim = '';

        for (let i = 0; i < event.results.length; i += 1) {
          const result = event.results[i];
          const text = (result[0]?.transcript || '').trim();
          if (!text) continue;
          if (result.isFinal) {
            committed = committed ? `${committed} ${text}` : text;
          } else {
            interim = interim ? `${interim} ${text}` : text;
          }
        }

        onTranscriptRef.current?.({ finalText: committed, interimText: interim, sessionId });
      };

      recognition.onerror = (event) => {
        if (sessionId !== sessionIdRef.current) {
          return;
        }
        if (event.error === 'aborted') {
          return;
        }
        if (event.error === 'no-speech') {
          return;
        }
        console.warn('Speech recognition error:', event.error);
        manualStopRef.current = true;
        keepListeningRef.current = false;
        clearRestartTimer();
        recognitionRef.current = null;
        setListeningState(false);
      };

      recognition.onend = () => {
        if (sessionId !== sessionIdRef.current) {
          return;
        }
        recognitionRef.current = null;

        if (manualStopRef.current || !keepListeningRef.current) {
          keepListeningRef.current = false;
          setListeningState(false);
          return;
        }

        clearRestartTimer();
        restartTimerRef.current = window.setTimeout(() => {
          restartTimerRef.current = null;
          if (manualStopRef.current || !keepListeningRef.current) {
            setListeningState(false);
            return;
          }
          const Ctor = getRecognitionCtor();
          if (!Ctor) {
            setListeningState(false);
            return;
          }
          try {
            const next = new Ctor();
            next.lang = lang;
            next.continuous = true;
            next.interimResults = true;
            attachRecognitionHandlers(next, sessionId);
            next.start();
            recognitionRef.current = next;
            setListeningState(true);
          } catch (e) {
            console.warn('Speech recognition restart failed:', e);
            keepListeningRef.current = false;
            setListeningState(false);
          }
        }, 150);
      };
    },
    [clearRestartTimer, lang, setListeningState]
  );

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      return false;
    }

    stop();
    manualStopRef.current = false;
    keepListeningRef.current = true;

    sessionIdRef.current += 1;
    const sessionId = sessionIdRef.current;

    const recognition = new Ctor();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;

    attachRecognitionHandlers(recognition, sessionId);

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setListeningState(true);
      return sessionId;
    } catch (e) {
      console.warn('Speech recognition start failed:', e);
      keepListeningRef.current = false;
      setListeningState(false);
      return false;
    }
  }, [attachRecognitionHandlers, lang, setListeningState, stop]);

  const toggle = useCallback(() => {
    if (listening) {
      stop();
      return;
    }
    start();
  }, [listening, start, stop]);

  useEffect(
    () => () => {
      manualStopRef.current = true;
      keepListeningRef.current = false;
      clearRestartTimer();
      try {
        recognitionRef.current?.stop();
      } catch (_) {
        /* ignore */
      }
    },
    [clearRestartTimer]
  );

  return { listening, supported, start, stop, toggle };
}

export default useSpeechToText;
